import type { ApiFilestackImageData } from '#/api/types/ApiFilestackImageData';
import { ArrowButton } from '#/components/ArrowButton/ArrowButton';
import { Box } from '#/components/Box/Box';
import { Button } from '#/components/Button/Button';
import { Body } from '#/components/Text/Body';
import { useHideDocumentScroll } from '#/hooks/useHideDocumentScroll';
import { useOnWindowKeyDown } from '#/hooks/useOnWindowKeyDown';
import type { CmsApiLink } from '#/modules/cms/api/types/CmsApiLink';
import { CmsButtonLoadable } from '#/modules/cms/components/Button/CmsButtonLoadable';

import {
  CmsTileModalBackdrop,
  CmsTileModalContent,
  CmsTileModalDesktopArrowHolder,
  CmsTileModalHolder,
  CmsTileModalTwoColumns,
  CmsTileModalTwoColumnsContent,
} from './CmsTileModal.styled';
import { CmsTileModalDesktopHeader } from './DesktopHeader/CmsTileModalDesktopHeader';
import { CmsTileModalLeftContent } from './LeftContent/CmsTileModalLeftContent';
import { CmsTileModalMobileHeader } from './MobileHeader/CmsTileModalMobileHeader';
import { CmsTileModalRightContent } from './RightContent/CmsTileModalRightContent';

type Props = {
  blockId: string;
  sectionIndex: number;
  heading: string;
  subHeading: string | null;
  description: string;
  caption?: string;
  imageData: ApiFilestackImageData;
  buttonData?: CmsApiLink;
  onClose: () => void;
  hasPrevious: boolean;
  onPrevious: () => void;
  hasNext: boolean;
  onNext: () => void;
  analyticsData: {
    eventName: string;
    eventProperties?: Record<string, unknown>;
  };
};

export function CmsTileModal({
  blockId,
  sectionIndex,
  heading,
  subHeading,
  description,
  caption,
  imageData,
  buttonData,
  onClose,
  hasPrevious,
  onPrevious,
  hasNext,
  onNext,
  analyticsData,
}: Props) {
  useHideDocumentScroll();
  useOnWindowKeyDown(['Escape'], onClose);

  return (
    <>
      {/* oxlint-disable-next-line jsx-a11y/prefer-tag-over-role */}
      <CmsTileModalBackdrop role="button" onClick={onClose} />

      <CmsTileModalHolder>
        <CmsTileModalMobileHeader
          hasPrevious={hasPrevious}
          onPrevious={onPrevious}
          hasNext={hasNext}
          onNext={onNext}
          onClose={onClose}
        />

        <CmsTileModalDesktopHeader onClose={onClose} />

        <CmsTileModalContent>
          <CmsTileModalDesktopArrowHolder>
            <ArrowButton
              direction="left"
              onClick={onPrevious}
              disabled={!hasPrevious}
            />
          </CmsTileModalDesktopArrowHolder>

          <CmsTileModalTwoColumns>
            <CmsTileModalTwoColumnsContent>
              <CmsTileModalLeftContent
                imageData={imageData}
                caption={caption}
              />

              <CmsTileModalRightContent
                heading={heading}
                subHeading={subHeading}
              >
                <Body
                  analyticsData={analyticsData}
                  dangerouslySetInnerHTML={{ __html: description }}
                  styleAs="bodySmall"
                />

                {buttonData ? (
                  <Box mt={[24, null, 36]}>
                    <CmsButtonLoadable
                      blockId={blockId}
                      sectionIndex={sectionIndex}
                      buttonData={buttonData}
                      eventProperties={{
                        button_text: buttonData.text,
                        ...analyticsData.eventProperties,
                      }}
                      render={({ data, children, title, qaId }) => (
                        <Button
                          data={data}
                          variant="tertiary"
                          iconPosition="right"
                          icon={{ type: 'normalized', name: 'arrow-right' }}
                          noPadding
                          title={title}
                          qaId={qaId}
                        >
                          {children}
                        </Button>
                      )}
                    />
                  </Box>
                ) : null}
              </CmsTileModalRightContent>
            </CmsTileModalTwoColumnsContent>
          </CmsTileModalTwoColumns>

          <CmsTileModalDesktopArrowHolder>
            <ArrowButton
              direction="right"
              onClick={onNext}
              disabled={!hasNext}
            />
          </CmsTileModalDesktopArrowHolder>
        </CmsTileModalContent>
      </CmsTileModalHolder>
    </>
  );
}
